import axios from 'axios';

const PORTAL_API_BASE = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '/ip-portal/api')
  : 'http://localhost:8000/ip-portal/api';

const portalApi = axios.create({
  baseURL: PORTAL_API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Attach portal token from sessionStorage on every request
portalApi.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('ip_portal_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Auth ──

export const PortalAuthAPI = {
  setup(data: { setup_key: string; name: string; email: string }) {
    return portalApi.post('/auth/setup', data);
  },
  setupVerifyTotp(data: { admin_id: string; code: string }) {
    return portalApi.post('/auth/setup/verify-totp', data);
  },
  setupRegisterDeviceStart(data: { admin_id: string }) {
    return portalApi.post('/auth/setup/register-device/start', data);
  },
  setupRegisterDeviceFinish(data: { admin_id: string; credential: any; device_name: string }) {
    return portalApi.post('/auth/setup/register-device/finish', data);
  },
  loginStart(data: { email: string }) {
    return portalApi.post('/auth/login/start', data);
  },
  loginFinish(data: { email: string; credential: any }) {
    return portalApi.post('/auth/login/finish', data);
  },
  loginTotp(data: { code: string }) {
    return portalApi.post('/auth/login/totp', data);
  },
};

// ── Whitelist ──

export const WhitelistAPI = {
  list() {
    return portalApi.get('/whitelist/');
  },
  add(data: { ip_address: string; label?: string }) {
    return portalApi.post('/whitelist/', data);
  },
  remove(ipId: string) {
    return portalApi.delete(`/whitelist/${ipId}`);
  },
};

// ── Devices ──

export const DevicesAPI = {
  list() {
    return portalApi.get('/devices/');
  },
  registerStart() {
    return portalApi.post('/devices/register/start');
  },
  registerFinish(data: { credential: any; device_name: string }) {
    return portalApi.post('/devices/register/finish', data);
  },
  deactivate(deviceId: string) {
    return portalApi.delete(`/devices/${deviceId}`);
  },
};

// ── Admin ──

export const AdminAPI = {
  getMe() {
    return portalApi.get('/admin/me');
  },
  listAdmins() {
    return portalApi.get('/admin/list');
  },
  invite(data: { email: string }) {
    return portalApi.post('/admin/invite', data);
  },
  acceptInvite(data: { invite_token: string; name: string }) {
    return portalApi.post('/admin/invite/accept', data);
  },
  acceptInviteVerifyTotp(data: { admin_id: string; code: string }) {
    return portalApi.post('/admin/invite/accept/verify-totp', data);
  },
  acceptInviteRegisterDeviceStart(data: { admin_id: string }) {
    return portalApi.post('/admin/invite/accept/register-device/start', data);
  },
  acceptInviteRegisterDeviceFinish(data: { admin_id: string; credential: any; device_name: string }) {
    return portalApi.post('/admin/invite/accept/register-device/finish', data);
  },
  getAuditLogs(page = 1, perPage = 50) {
    return portalApi.get('/admin/audit-logs', { params: { page, per_page: perPage } });
  },
  deactivateAdmin(adminId: string) {
    return portalApi.delete(`/admin/${adminId}`);
  },
  reactivateAdmin(adminId: string) {
    return portalApi.post(`/admin/${adminId}/reactivate`);
  },
  deleteAdmin(adminId: string) {
    return portalApi.delete(`/admin/${adminId}/permanent`);
  },
};

// ── Public ──

export const PublicAPI = {
  getMyIp() {
    return portalApi.get('/my-ip');
  },
};

export default portalApi;
